import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Store, Heart, Download, Eye, Tag, Filter, TrendingUp, Clock, Gift } from "lucide-react";
import { useTr } from "@/i18n/translator";

const INDUSTRIES = ["todos", "cafe", "ecommerce", "saas", "servicios", "retail", "tech"];
const PLATFORMS = ["instagram", "tiktok", "twitter", "facebook", "linkedin"];
const LANGS = [
  { code: "", label: "Todos" },
  { code: "es-MX", label: "Español" },
  { code: "en", label: "English" },
  { code: "pt-BR", label: "Português" },
];

export default function Marketplace() {
  const tr = useTr();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [industry, setIndustry] = useState("todos");
  const [platform, setPlatform] = useState("");
  const [language, setLanguage] = useState("");
  const [sort, setSort] = useState("popular");
  const [q, setQ] = useState("");
  const [freeOnly, setFreeOnly] = useState(false);
  const [using, setUsing] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (industry && industry !== "todos") params.set("industry", industry);
      if (platform) params.set("platform", platform);
      if (language) params.set("language", language);
      if (q) params.set("q", q);
      if (freeOnly) params.set("max_price", "0");
      params.set("sort", sort);
      const { data } = await api.get(`/templates/marketplace?${params.toString()}`);
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [industry, platform, language, sort, freeOnly]);

  const applyTemplate = async (id) => {
    setUsing(id);
    try {
      const { data } = await api.post(`/templates/${id}/use`);
      sessionStorage.setItem("cia_template_draft", JSON.stringify(data));
      toast.success(tr("Plantilla cargada en el generador"));
      nav("/dashboard/generator");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al usar plantilla");
    } finally { setUsing(null); }
  };

  return (
    <div className="space-y-6" data-testid="marketplace-page">
      <div>
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("DOSSIER · MARKETPLACE · PLANTILLAS")}</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">Marketplace</h1>
        <p className="mt-3 font-mono-data text-sm text-[#5C5C5C] max-w-2xl">
          Copia plantillas que ya funcionaron en otras marcas. Filtros por industria, red e idioma.
          Cada plantilla se clona a tu generador lista para editar y publicar.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#0A0A0A] p-4 grid md:grid-cols-[1fr_auto_auto_auto_auto] gap-3 items-end">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Buscar plantillas…"
            className="w-full bg-white border border-[#0A0A0A] pl-10 pr-3 py-2 font-mono-data text-sm"
            data-testid="marketplace-search"
          />
        </div>
        <Select label={tr("Industria")} value={industry} onChange={setIndustry} options={INDUSTRIES.map((i) => ({ value: i, label: i }))} testid="filter-industry" />
        <Select label={tr("Red")} value={platform} onChange={setPlatform} options={[{ value: "", label: tr("todas") }, ...PLATFORMS.map((p) => ({ value: p, label: p }))]} testid="filter-platform" />
        <Select label={tr("Idioma")} value={language} onChange={setLanguage} options={LANGS.map((l) => ({ value: l.code, label: l.label }))} testid="filter-language" />
        <Select label={tr("Orden")} value={sort} onChange={setSort} options={[{ value: "popular", label: tr("Populares") }, { value: "recent", label: tr("Recientes") }, { value: "free", label: tr("Gratis") }]} testid="filter-sort" />
      </div>

      {loading ? (
        <div className="text-center font-mono-data text-sm text-[#5C5C5C] py-20">{tr("Cargando plantillas…")}</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#0A0A0A] p-10 text-center" data-testid="marketplace-empty">
          <Store className="w-12 h-12 mx-auto text-[#5C5C5C]" />
          <div className="font-display text-2xl mt-3">{tr("El marketplace está apenas arrancando.")}</div>
          <p className="font-mono-data text-sm text-[#5C5C5C] mt-2 max-w-md mx-auto">
            {tr("Sé el primero en publicar una plantilla — desde el Generador, guarda tu mejor post como plantilla pública y ayuda a otras marcas a crecer.")}
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t) => (
            <div key={t.id} className="bg-white border border-[#0A0A0A] flex flex-col" data-testid={`template-${t.id}`}>
              {t.image_url ? (
                <img src={t.image_url} alt={t.name} className="w-full aspect-square object-cover border-b border-[#0A0A0A]" />
              ) : (
                <div className="w-full aspect-square bg-[#F4F4F0] border-b border-[#0A0A0A] flex items-center justify-center">
                  <Store className="w-10 h-10 text-[#5C5C5C]" />
                </div>
              )}
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-center justify-between">
                  <span className="font-mono-mark text-[9px] text-[#5C5C5C] uppercase">{t.industry} · {(t.platforms?.[0] || "todas")}</span>
                  {t.price_usd > 0 ? (
                    <span className="font-mono-mark text-[9px] bg-[#FFD600] px-2 py-0.5">${t.price_usd}</span>
                  ) : (
                    <span className="font-mono-mark text-[9px] bg-[#0A0A0A] text-[#FFD600] px-2 py-0.5">{tr("GRATIS")}</span>
                  )}
                </div>
                <div className="font-display text-lg mt-2 line-clamp-2">{t.name}</div>
                <p className="font-mono-data text-xs text-[#5C5C5C] mt-2 line-clamp-3 flex-1">{t.body}</p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="font-mono-data text-[10px] text-[#5C5C5C] flex items-center gap-1"><Download className="w-3 h-3" /> {t.purchases_count || 0}</span>
                  <button
                    onClick={() => applyTemplate(t.id)}
                    disabled={using === t.id}
                    className="font-mono-mark text-[10px] bg-[#E53935] text-white px-3 py-2 border border-[#E53935] disabled:opacity-50"
                    data-testid={`template-use-${t.id}`}
                  >
                    {using === t.id ? "…" : "USAR"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="font-mono-mark text-[9px] text-[#5C5C5C]">{label.toUpperCase()}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="block mt-1 bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm"
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
